<template>
  <AdminPageView :loading :loadingErrorMessage :actionsOnlyOnTop="true" :title="`Participants ${travel?.name ? travel?.name : ''}`">
    <div v-if="travel" class="table-responsive">

      <!-- Barre de contrôles -->
      <div class="d-flex flex-wrap gap-2 align-items-center mt-3 mb-2">
        <!-- Recherche texte -->
        <div class="input-group" style="max-width: 280px;">
          <span class="input-group-text"><i class="fa-solid fa-magnifying-glass"></i></span>
          <input
            v-model="searchText"
            type="text"
            class="form-control"
            placeholder="Rechercher..."
          />
          <button v-if="searchText" class="btn btn-outline-secondary" @click="searchText = ''">
            <i class="fa-solid fa-xmark"></i>
          </button>
        </div>

        <!-- Filtre par statut -->
        <select v-model="statusFilter" class="form-select" style="max-width: 250px;">
          <option value="all">Tous les statuts</option>
          <option value="inscrit">Inscrit</option>
          <option value="absent">Absent</option>
          <option value="non-inscrit">Non inscrit</option>
          <option value="invitation-envoyee">Invitation envoyée</option>
          <option value="non-envoye">Invitation non envoyée</option>
        </select>

        <!-- Sélecteur de colonnes -->
        <BDropdown variant="outline-secondary" no-caret>
          <template #button-content>
            <i class="fa-solid fa-table-columns me-1"></i> Colonnes
          </template>
          <BDropdownForm>
            <div v-for="col in toggleableColumns" :key="col.key" class="form-check mb-1">
              <input
                class="form-check-input"
                type="checkbox"
                :id="`col-${col.key}`"
                :checked="visibleColumns.includes(col.key)"
                @change="toggleColumn(col.key)"
              />
              <label class="form-check-label" :for="`col-${col.key}`">{{ col.label }}</label>
            </div>
          </BDropdownForm>
        </BDropdown>

        <!-- Masquer les organisateurs -->
        <SwitchComponent v-model="hideOrganizers" label="Masquer les organisateurs" class="ms-1" />

        <!-- Masquer les accompagnants -->
        <SwitchComponent v-model="hideCompanions" label="Masquer les accompagnants" />

        <!-- Compteur résultats -->
        <span class="text-muted ms-auto small">
          {{ filteredAndSortedParticipations.length }}
          <template v-if="filteredAndSortedParticipations.length !== (travel.participations?.filter(p => p && p.user)?.length ?? 0)">
            / {{ travel.participations?.filter(p => p && p.user)?.length ?? 0 }}
          </template>
          participant{{ filteredAndSortedParticipations.length !== 1 ? 's' : '' }}
          <template v-if="filteredCompanionsCount > 0">
            + {{ filteredCompanionsCount }} accompagnant{{ filteredCompanionsCount !== 1 ? 's' : '' }}
          </template>
        </span>
      </div>

      <table class="table table-striped">
        <thead>
          <tr>
            <th scope="col"><input class="form-check-input" type="checkbox" @click="selectAllParticipants" v-model="allParticipantsSelected" /></th>
            <th
              v-if="visibleColumns.includes('nom')"
              scope="col"
              class="sortable-col"
              :class="{ 'sort-active': sortColumn === 'nom' }"
              @click="toggleSort('nom')"
            >
              Nom <SortIconComponent column="nom" :sortColumn :sortDirection />
            </th>
            <th
              v-if="visibleColumns.includes('email')"
              scope="col"
              class="sortable-col"
              :class="{ 'sort-active': sortColumn === 'email' }"
              @click="toggleSort('email')"
            >
              Email <SortIconComponent column="email" :sortColumn :sortDirection />
            </th>
            <th
              v-if="visibleColumns.includes('mobilePhone')"
              scope="col"
              class="sortable-col"
              :class="{ 'sort-active': sortColumn === 'mobilePhone' }"
              @click="toggleSort('mobilePhone')"
            >
              Téléphone <SortIconComponent column="mobilePhone" :sortColumn :sortDirection />
            </th>
            <th
              v-if="visibleColumns.includes('entreprise')"
              scope="col"
              class="sortable-col"
              :class="{ 'sort-active': sortColumn === 'entreprise' }"
              @click="toggleSort('entreprise')"
            >
              Entreprise <SortIconComponent column="entreprise" :sortColumn :sortDirection />
            </th>
            <th
              v-if="visibleColumns.includes('statut')"
              scope="col"
              class="sortable-col"
              :class="{ 'sort-active': sortColumn === 'statut' }"
              @click="toggleSort('statut')"
            >
              Statut <SortIconComponent column="statut" :sortColumn :sortDirection />
            </th>
            <th
              v-if="visibleColumns.includes('signature')"
              scope="col"
              class="sortable-col"
              :class="{ 'sort-active': sortColumn === 'signature' }"
              @click="toggleSort('signature')"
            >
              Signature <SortIconComponent column="signature" :sortColumn :sortDirection />
            </th>
            <th scope="col" class="text-center">Actions</th>
          </tr>
        </thead>
        <tbody>
          <template v-if="filteredAndSortedParticipations.length > 0">
            <!-- Section organisateurs -->
            <template v-if="organizerParticipations.length > 0">
              <tr class="table-secondary">
                <th :colspan="tableColspan" class="py-1 ps-2 small fw-semibold text-secondary">
                  <i class="fa-solid fa-star me-1"></i> Équipe organisation ({{ organizerParticipations.length }})
                </th>
              </tr>
              <ParticipationRowComponent
                v-for="participation in organizerParticipations"
                :key="participation.id"
                :participation
                :visibleColumns
                :checkedParticipants
                :hideCompanions="hideCompanions"
                rowClass="table-warning"
                @update:checkedParticipants="checkedParticipants = $event"
                @delete="deleteParticipant"
              />
            </template>
            <!-- Section participants réguliers -->
            <template v-if="regularParticipations.length > 0">
              <tr v-if="organizerParticipations.length > 0" class="table-secondary">
                <th :colspan="tableColspan" class="py-1 ps-2 small fw-semibold text-secondary">
                  <i class="fa-solid fa-users me-1"></i> Participants ({{ regularParticipations.length }})
                </th>
              </tr>
              <ParticipationRowComponent
                v-for="participation in regularParticipations"
                :key="participation.id"
                :participation
                :visibleColumns
                :checkedParticipants
                :hideCompanions="hideCompanions"
                @update:checkedParticipants="checkedParticipants = $event"
                @delete="deleteParticipant"
              />
            </template>
          </template>
          <tr v-else>
            <td :colspan="tableColspan" class="text-center text-muted py-4">
              <i class="fa-solid fa-filter me-2"></i>Aucun participant ne correspond aux filtres appliqués.
            </td>
          </tr>
        </tbody>
      </table>

      <div v-if="checkedParticipants.length > 0" class="mt-2 float-end d-flex flex-column align-items-end">
        <div class="d-flex gap-2">
          <button @click="sendInvitation" type="button" class="btn btn-link p-0"><i class="fa-solid fa-paper-plane me-2"></i><span>Envoyer l'invitation <span v-if="checkedParticipants.length == 1">au participant sélectionné</span><span v-else>aux {{checkedParticipants.length}} participants sélectionnés</span></span></button>
          <button v-if="travel?.reminderTemplateId" @click="sendReminder" type="button" class="btn btn-link p-0"><i class="fa-solid fa-bell me-2"></i><span>Envoyer une relance <span v-if="checkedParticipants.length == 1">au participant sélectionné</span><span v-else>aux {{checkedParticipants.length}} participants sélectionnés</span></span></button>
          <button @click="openEmailModal" type="button" class="btn btn-link p-0"><i class="fa-solid fa-envelope me-2"></i><span>Envoyer un email <span v-if="checkedParticipants.length == 1">au participant sélectionné</span><span v-else>aux {{checkedParticipants.length}} participants sélectionnés</span></span></button>
          <!-- <span class="text-muted small">
            {{ checkedParticipants.length }} participant{{ checkedParticipants.length > 1 ? 's' : '' }} sélectionné{{ checkedParticipants.length > 1 ? 's' : '' }}
          </span> -->
          <BDropdown
            variant="link"
            :disabled="downloadingPdfs || downloadingDocuments || downloadingSignatures"
          >
            <template #button-content>
              <span v-if="downloadingPdfs || downloadingDocuments || downloadingSignatures">
                <span class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" ></span>Téléchargement…
              </span>
              <span v-else>
                <i class="fa-solid fa-download me-1"></i>Télécharger
              </span>
            </template>

            <!-- PDF générés -->
            <BDropdownItem @click="downloadBulkPdfs">
              <i class="fa-solid fa-file-pdf me-2 text-muted"></i>PDF des participants
            </BDropdownItem>

            <!-- Documents signés -->
            <BDropdownItem @click="downloadBulkSignatures">
              <i class="fa-solid fa-pen-nib me-2 text-muted"></i>Documents signés
            </BDropdownItem>

            <!-- Documents uploadés — un sous-item par type -->
            <template v-if="travel.documents && travel.documents.length > 0">
              <BDropdownDivider />
              <BDropdownText class="text-muted small px-3 py-1">Documents uploadés</BDropdownText>
              <BDropdownItem
                v-for="doc in travel.documents"
                :key="doc.id"
                @click="downloadBulkDocuments(doc.id)"
              >
                <i class="fa-solid fa-file-arrow-down me-2 text-muted"></i>{{ doc.label }}
              </BDropdownItem>
            </template>
          </BDropdown>
      </div>
      <div class="d-flex gap-2">
        <button @click="deleteCheckedParticipants" type="button" class="btn btn-link p-0 text-danger" v-if="checkedParticipants.length > 1">
          <i class="fa-regular fa-trash-can me-2"></i>
          <span>Supprimer
            <span v-if="checkedParticipants.length == 1">le participant sélectionné</span>
            <span v-else>les {{checkedParticipants.length}} participants sélectionnés</span>
          </span>
        </button>
      </div>
    </div>
  </div>

    <div class="card mt-3">
      <div class="card-header bg-primary">Ajouter un participant</div>
      <div class="card-body">
        <div class="mb-3">
          <AutocompleteComponent
            label="Rechercher un participant à ajouter"
            noElementFoundText="Aucun participant trouvé"
            @search-change="searchParticipantChange"
            @select="participantToAddSelected"
            :options="participantsToAddList"
            options-track-by="id"
            :resetAfter="true"
            :options-label-function="(obj) => `${obj.lastName?.toUpperCase()} ${obj.firstName}`"
          />
        </div>
        <div class="float-end text-end">
          <router-link :to="{ name: 'admin-new-participant', query: { travelId: props.id } }">
            <button class="btn btn-outline-primary me-3">
              <i class="fa-solid fa-plus"></i> Créer un nouveau participant
            </button>
          </router-link>
          <button class="btn btn-outline-primary" @click="triggerExcelImport" :disabled="importingExcel">
            <span v-if="importingExcel" class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" ></span>
            <i class="fa-solid fa-file-excel"></i> Importer des participants
          </button>
          <div class="small fw-lighter fst-italic">Le fichier Excel à importer doit comporter 4 colonnes dans cet ordre : Nom, Prénom, Email, Téléphone</div>
          <input
            ref="excelImportInput"
            type="file"
            accept=".xlsx"
            class="d-none"
            @change="onExcelFileSelected"
          />
        </div>
      </div>
    </div>
  </AdminPageView>

  <BModal
    v-model="showEmailModal"
    title="Envoyer un email"
    ok-title="Envoyer"
    cancel-title="Annuler"
    :ok-disabled="selectedEmailTemplateId === null || sendingEmail || loadingEmailTemplates"
    @ok="sendEmail"
  >
    <p class="mb-3">
      Destinataires : {{ checkedParticipants.length }} participant{{ checkedParticipants.length > 1 ? 's' : '' }} sélectionné{{ checkedParticipants.length > 1 ? 's' : '' }}.
      Les participants sans adresse email sont ignorés.
    </p>
    <div v-if="loadingEmailTemplates" class="text-center py-3">
      <span class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>Chargement des templates…
    </div>
    <template v-else>
      <p v-if="emailTemplates.length === 0" class="text-muted mb-0">Aucun template Brevo actif n'est disponible.</p>
      <BFormGroup v-else label="Template Brevo" label-for="admin-email-template">
        <BFormSelect id="admin-email-template" v-model="selectedEmailTemplateId" :options="emailTemplateOptions" />
      </BFormGroup>
    </template>
  </BModal>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue';
import { GetCivilityShortLabel, getFileUrl, getAdminWebInscriptionUrl, useConfirmDialog } from '@/compositions/helper';

import adminApi from '@/api/adminApi'
import AdminPageView from '@/views/admin/AdminPageView.vue';
import ParticipationRowComponent from '@/components/admin/ParticipationRowComponent.vue';
import SortIconComponent from '@/components/common/SortIconComponent.vue';

import { useNotificationsStore } from '@/stores/notificationStore';

// ─── Types ───────────────────────────────────────────────────────────────────

type ColumnKey = 'nom' | 'email' | 'mobilePhone' | 'entreprise' | 'statut' | 'signature';
type SortDirection = 'asc' | 'desc';

interface ParticipantsPreferences {
  visibleColumns: ColumnKey[];
  sortColumn: ColumnKey | null;
  sortDirection: SortDirection;
}

// ─── Constantes ──────────────────────────────────────────────────────────────

const LS_KEY = 'mynemo_admin-participants-preferences';

const toggleableColumns: { key: ColumnKey; label: string }[] = [
  { key: 'nom', label: 'Nom' },
  { key: 'email', label: 'Email' },
  { key: 'mobilePhone', label: 'Téléphone' },
  { key: 'entreprise', label: 'Entreprise' },
  { key: 'statut', label: 'Statut' },
  { key: 'signature', label: 'Signature' },
];

const DEFAULT_PREFERENCES: ParticipantsPreferences = {
  visibleColumns: ['nom', 'email', 'mobilePhone', 'entreprise', 'statut','signature'],
  sortColumn: null,
  sortDirection: 'asc',
};

// ─── Stores / composables ─────────────────────────────────────────────────────

const notificationsStore = useNotificationsStore();
const { confirmDialog } = useConfirmDialog();

// ─── State ────────────────────────────────────────────────────────────────────

const loading = ref(true);
const loadingErrorMessage = ref<string | undefined>(undefined);

const travel = ref<AdminTravel>();
const participantsToAddList = ref<AdminUser[]>([]);

const props = defineProps<{ id?: number }>();
const checkedParticipants = ref<number[]>([]);

// Import Excel
const excelImportInput = ref<HTMLInputElement | null>(null)
const importingExcel = ref(false)

const triggerExcelImport = () => {
  excelImportInput.value?.click()
}

const onExcelFileSelected = async (event: Event) => {
  const input = event.target as HTMLInputElement
  const file = input.files?.[0]
  if (!file || !props.id) return
  input.value = ''

  importingExcel.value = true
  try {
    const response = await adminApi.importParticipantsExcel(props.id, file)
    const r = response.data
    const parts: string[] = []
    if (r.added??0 > 0) parts.push(`${r.added} ajouté${r.added??0 > 1 ? 's' : ''}`)
    if (r.created??0 > 0) parts.push(`${r.created} compte${r.created??0 > 1 ? 's' : ''} créé${r.created??0 > 1 ? 's' : ''}`)
    if (r.alreadyPresent??0 > 0) parts.push(`${r.alreadyPresent} déjà présent${r.alreadyPresent??0 > 1 ? 's' : ''}`)
    if (r.errors??0 > 0) parts.push(`${r.errors} erreur${r.errors??0 > 1 ? 's' : ''}`)

    const summary = parts.length > 0 ? parts.join(', ') : 'Aucune modification'
    const errorDetails = r.errorMessages?.length??0 > 0 ? r.errorMessages?.join('\n') : undefined

    if (r.errors??0 > 0) {
      notificationsStore.notifyError(`Import terminé : ${summary}`, errorDetails)
    } else {
      notificationsStore.notifySuccess(`Import terminé : ${summary}`)
    }

    load()
  } catch {
    notificationsStore.notifyError('Erreur lors de l\'import du fichier Excel')
  } finally {
    importingExcel.value = false
  }
}

// Filtres (non persistés)
const searchText = ref('');
const statusFilter = ref<'all' | 'inscrit' | 'absent' | 'invitation-envoyee' | 'non-envoye' | 'non-inscrit'>('all');
const hideOrganizers = ref(false);
const hideCompanions = ref(false);

// Préférences persistées
const visibleColumns = ref<ColumnKey[]>([...DEFAULT_PREFERENCES.visibleColumns]);
const sortColumn = ref<ColumnKey | null>(DEFAULT_PREFERENCES.sortColumn);
const sortDirection = ref<SortDirection>(DEFAULT_PREFERENCES.sortDirection);

// ─── localStorage ─────────────────────────────────────────────────────────────

const loadPreferences = () => {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (raw) {
      const prefs: ParticipantsPreferences = JSON.parse(raw);
      if (Array.isArray(prefs.visibleColumns) && prefs.visibleColumns.length > 0) {
        visibleColumns.value = prefs.visibleColumns;
      }
      if (prefs.sortColumn !== undefined) {
        sortColumn.value = prefs.sortColumn;
      }
      if (prefs.sortDirection === 'asc' || prefs.sortDirection === 'desc') {
        sortDirection.value = prefs.sortDirection;
      }
    }
  } catch {
    // localStorage indisponible ou JSON invalide — on ignore
  }
};

const savePreferences = () => {
  try {
    const prefs: ParticipantsPreferences = {
      visibleColumns: visibleColumns.value,
      sortColumn: sortColumn.value,
      sortDirection: sortDirection.value,
    };
    localStorage.setItem(LS_KEY, JSON.stringify(prefs));
  } catch {
    // localStorage indisponible — on ignore
  }
};

// ─── Computed ─────────────────────────────────────────────────────────────────

const getParticipationStatus = (participation: ParticipationDb): string => {
  if (participation.invitation?.completionDate) return 'inscrit';
  if (participation.invitation?.refusalDate) return 'absent';
  if (participation.invitation?.sentDate) return 'invitation-envoyee';
  return 'non-envoye';
};

const isOrganizer = (participation: ParticipationDb): boolean => {
  const userInfo = participation.userInfo;
  const isAdmin = participation.user?.isAdmin === true

  return userInfo?.status === UserStatusEnum.Organisation || (userInfo?.status == null && isAdmin);
};

const getParticipationSearchText = (participation: ParticipationDb): string => {
  const parts: string[] = [];
  const user = participation.user!;

  if (visibleColumns.value.includes('nom')) {
    parts.push(GetCivilityShortLabel(user.civility) ?? '');
    parts.push(user.firstName ?? '');
    parts.push(user.lastName?.toUpperCase() ?? '');
  }
  if (visibleColumns.value.includes('email')) {
    parts.push(user.email ?? '');
  }
  if (visibleColumns.value.includes('mobilePhone')) {
    parts.push(user.mobilePhone ?? '');
    parts.push(user.mobilePhone?.replaceAll(' ', '') ?? '');
  }
  if (visibleColumns.value.includes('entreprise')) {
    parts.push(user.company ?? '');
  }
  if (visibleColumns.value.includes('statut')) {
    const statusLabels: Record<string, string> = {
      inscrit: 'inscrit',
      absent: 'absent',
      'invitation-envoyee': 'invitation envoyée',
      'non-envoye': 'non envoyé',
    };
    parts.push(statusLabels[getParticipationStatus(participation)] ?? '');
  }
  if (visibleColumns.value.includes('signature')) {
    parts.push(participation.signature?.signedDate ? 'signé' : 'non signé');
  }

  // Inclure les accompagnants dans la recherche
  if (!hideCompanions.value) {
    for (const companion of (participation.companions ?? [])) {
      if (visibleColumns.value.includes('nom')) {
        parts.push(GetCivilityShortLabel(companion.civility) ?? '');
        parts.push(companion.firstName ?? '');
        parts.push(companion.lastName?.toUpperCase() ?? '');
      }
      if (visibleColumns.value.includes('email')) {
        parts.push(companion.email ?? '');
      }
      if (visibleColumns.value.includes('mobilePhone')) {
        parts.push(companion.mobilePhone ?? '');
        parts.push(companion.mobilePhone?.replaceAll(' ', '') ?? '');
      }
      if (visibleColumns.value.includes('entreprise')) {
        parts.push(companion.company ?? '');
      }
    }
  }

  return parts.join(' ').toLowerCase();
};

const getSortValue = (participation: ParticipationDb, col: ColumnKey): string => {
  const user = participation.user!;
  switch (col) {
    case 'nom':
      return `${user.lastName ?? ''} ${user.firstName ?? ''}`.toLowerCase();
    case 'email':
      return (user.email ?? '').toLowerCase();
    case 'mobilePhone':
      return (user.mobilePhone ?? '').toLowerCase();
    case 'entreprise':
      return (user.company ?? '').toLowerCase();
    case 'statut':
      return getParticipationStatus(participation);
    case 'signature':
      return participation.signature?.signedDate ? participation.signature.signedDate.toISOString() : '';
    default:
      return '';
  }
};

const filteredAndSortedParticipations = computed((): ParticipationDb[] => {
  if (!travel.value?.participations) return [];

  let result = travel.value.participations.filter(
    (p): p is ParticipationDb & { user: NonNullable<ParticipationDb['user']> } =>
      !!(p && p.user),
  );

  // Filtre texte
  const needle = searchText.value.trim().toLowerCase();
  if (needle) {
    result = result.filter((p) => getParticipationSearchText(p).includes(needle));
  }

  // Filtre statut
  if (statusFilter.value !== 'all') {
    if (statusFilter.value === 'non-inscrit') {
      result = result.filter((p) => !['inscrit','absent'].includes(getParticipationStatus(p)));
    } else {
      result = result.filter((p) => getParticipationStatus(p) === statusFilter.value);
    }
  }

  // Filtre organisateurs
  if (hideOrganizers.value) {
    result = result.filter((p) => !isOrganizer(p));
  }

  // Tri
  if (sortColumn.value) {
    const col = sortColumn.value;
    const dir = sortDirection.value === 'asc' ? 1 : -1;
    result = [...result].sort((a, b) => {
      const va = getSortValue(a, col);
      const vb = getSortValue(b, col);
      return va < vb ? -dir : va > vb ? dir : 0;
    });
  } else {
    // On tri par défaut par nom
    result = [...result].sort((a, b) => {
      const va = getSortValue(a, 'nom');
      const vb = getSortValue(b, 'nom');
      return va < vb ? -1 : va > vb ? 1 : 0;
    });
  }

  return result;
});

const organizerParticipations = computed(() =>
  filteredAndSortedParticipations.value.filter(isOrganizer),
);

const regularParticipations = computed(() =>
  filteredAndSortedParticipations.value.filter((p) => !isOrganizer(p)),
);

const tableColspan = computed(() => visibleColumns.value.length + 2);

const filteredCompanionsCount = computed(() =>
  filteredAndSortedParticipations.value.reduce((sum, p) => sum + (p.companions?.length ?? 0), 0),
);

// ─── Actions colonnes / tri ───────────────────────────────────────────────────

const toggleColumn = (key: ColumnKey) => {
  if (visibleColumns.value.includes(key)) {
      visibleColumns.value = visibleColumns.value.filter((c) => c !== key);
      // Si on masque la colonne sur laquelle on trie, on réinitialise le tri
      if (sortColumn.value === key) {
        sortColumn.value = null;
      }
  } else {
    visibleColumns.value = [...visibleColumns.value, key];
  }
  savePreferences();
};

const toggleSort = (col: ColumnKey) => {
  if (sortColumn.value === col) {
    if (sortDirection.value === 'asc') {
      sortDirection.value = 'desc';
    } else {
      // 3e clic : supprime le tri
      sortColumn.value = null;
      sortDirection.value = 'asc';
    }
  } else {
    sortColumn.value = col;
    sortDirection.value = 'asc';
  }
  savePreferences();
};

// ─── Chargement données ───────────────────────────────────────────────────────

const load = () => {
  if (props.id) {
    adminApi
      .getTravel(props.id)
      .then((response) => {
        travel.value = response.data;
      })
      .catch((error: Error) => {
        loadingErrorMessage.value = "Impossible de charger l'événement";
        console.error('Error loading travel:', error);
      })
      .finally(() => {
        loading.value = false;
      });
  } else {
    loading.value = false;
  }
};

// ─── Sélection participants ───────────────────────────────────────────────────

const allParticipantsSelected = computed(() => {
  if (filteredAndSortedParticipations.value.length > 0) {
    return (
      filteredAndSortedParticipations.value.length > 0 &&
      checkedParticipants.value.length === filteredAndSortedParticipations.value.length
    );
  }
  return false;
});

const selectAllParticipants = () => {
  if (filteredAndSortedParticipations.value.length > 0) {
    if (!allParticipantsSelected.value) {
      checkedParticipants.value = filteredAndSortedParticipations.value.map((participation) => participation.user!.id);
    } else {
      checkedParticipants.value = [];
    }
  }
};

// ─── Actions participants ─────────────────────────────────────────────────────
const deleteParticipant = async (id: number) => {
  if (travel.value && travel.value.participations) {
    const user = travel.value.participations.find(
      (participation) => participation.user?.id === id,
    )?.user;
    if (
      await confirmDialog(
        `Êtes-vous sûr de vouloir supprimer le participant${user ? ` ${user.firstName} ${user.lastName?.toUpperCase()} (${user.email})` : ''} de cette événement ?`,
      )
    ) {
      adminApi
        .deleteParticipant(id, travel.value.id)
        .then(() => {
          if (travel.value && travel.value.participations) {
            travel.value.participations = travel.value.participations.filter(
              (participation: ParticipationDb) => participation.user?.id !== id,
            );
          } else {
            notificationsStore.notifyError(
              'Erreur lors de la suppression du participant.',
              'Participant introuvable.',
            );
          }
          notificationsStore.notifySuccess(
            'Participant supprimé',
            'Le participant a été supprimé avec succès.',
          );
        })
        .catch((error: Error) => {
          notificationsStore.notifyError(
            'Erreur lors de la suppression du participant.',
            error.message,
          );
        });
    }
  } else {
    notificationsStore.notifyError(
      'Erreur lors de la suppression du participant.',
      'Participant introuvable.',
    );
  }
};

const deleteCheckedParticipants = async () => {
  if (!travel.value || checkedParticipants.value.length === 0) return;

  const count = checkedParticipants.value.length;
  if (
    await confirmDialog(
      `Êtes-vous sûr de vouloir supprimer ${count === 1 ? 'le participant sélectionné' : `les ${count} participants sélectionnés`} de cet événement ?`,
    )
  ) {
    adminApi
      .deleteParticipants(travel.value.id, checkedParticipants.value)
      .then(() => {
        if (travel.value && travel.value.participations) {
          const removed = checkedParticipants.value;
          travel.value.participations = travel.value.participations.filter(
            (participation: ParticipationDb) => !removed.includes(participation.user!.id),
          );
          checkedParticipants.value = [];
        }
        notificationsStore.notifySuccess(
          'Participants supprimés',
          `${count} participant${count > 1 ? 's' : ''} supprimé${count > 1 ? 's' : ''} avec succès.`,
        );
      })
      .catch((error: Error) => {
        notificationsStore.notifyError(
          'Erreur lors de la suppression des participants.',
          error.message,
        );
      });
  }
};

const searchParticipantChange = (query: string) => {
  if (travel.value) {
    adminApi
      .searchParticipantToAdd(travel.value.id, query)
      .then((response) => {
        participantsToAddList.value = response.data;
      })
      .catch((error: Error) => {
        notificationsStore.notifyError(
          'Erreur lors de la recherche des participants.',
          error.message,
        );
      });
  } else {
    notificationsStore.notifyError(
      'Evénement non chargé. Impossible de rechercher des participants.',
    );
  }
};

const participantToAddSelected = (selectedOption: unknown) => {
  const optionId = (selectedOption as AdminUser).id;
  if (travel.value && selectedOption && optionId) {
    adminApi
      .addParticipant(optionId, travel.value.id)
      .then((response) => {
        if (response.data) {
          if (travel.value && travel.value.participations) {
            travel.value.participations.push({
              user: selectedOption as AdminUser,
            } as ParticipationDb);
            participantsToAddList.value = [];
            notificationsStore.notifySuccess(
              'Participant ajouté',
              'Le participant a été ajouté avec succès.',
            );
          } else if (travel.value) {
            travel.value.participations = [
              { user: selectedOption as AdminUser } as ParticipationDb,
            ];
            participantsToAddList.value = [];
            notificationsStore.notifySuccess(
              'Participant ajouté',
              'Le participant a été ajouté avec succès.',
            );
          }
        }
      })
      .catch((error: Error) => {
        notificationsStore.notifyError(
          "Erreur lors de l'ajout du participant.",
          error.message,
        );
      });
  }
};

// ─── Téléchargements groupés ──────────────────────────────────────────────────

const downloadingPdfs = ref(false);
const downloadingDocuments = ref(false);
const downloadingSignatures = ref(false);

const DOWNLOAD_DELAY_MS = 500;

const triggerDownloadsSequentially = async (urls: string[]) => {
  for (let i = 0; i < urls.length; i++) {
    window.open(urls[i], '_blank');
    if (i < urls.length - 1) {
      await new Promise((resolve) => setTimeout(resolve, DOWNLOAD_DELAY_MS));
    }
  }
};

const downloadBulkPdfs = async () => {
  if (!travel.value || checkedParticipants.value.length === 0) return;

  const participationsWithPdf = filteredAndSortedParticipations.value.filter(
    (p) => checkedParticipants.value.includes(p.user!.id) && p.invitation?.completionDate,
  );

  if (participationsWithPdf.length === 0) {
    notificationsStore.notifyInfo(
      'Aucun PDF disponible',
      'Aucun des participants sélectionnés n\'est inscrit (PDF non disponible).',
    );
    return;
  }

  downloadingPdfs.value = true;
  try {
    const urls = participationsWithPdf.map(
      (p) => getAdminWebInscriptionUrl(travel.value!.id, p.user!.id, true),
    );
    await triggerDownloadsSequentially(urls);
    notificationsStore.notifySuccess(
      'Téléchargements lancés',
      `${urls.length} PDF téléchargé${urls.length > 1 ? 's' : ''}.`,
    );
  } finally {
    downloadingPdfs.value = false;
  }
};

const downloadBulkDocuments = async (documentId: number) => {
  if (!travel.value || checkedParticipants.value.length === 0) return;

  downloadingDocuments.value = true;
  try {
    const response = await adminApi.getParticipantDocuments(
      travel.value.id,
      checkedParticipants.value,
      documentId,
    );
    const files = response.data;

    if (files.length === 0) {
      notificationsStore.notifyInfo(
        'Aucun document disponible',
        'Aucun des participants sélectionnés n\'a uploadé ce document.',
      );
      return;
    }

    const urls = files.map((f) => getFileUrl(f.key,true));
    await triggerDownloadsSequentially(urls);
    notificationsStore.notifySuccess(
      'Téléchargements lancés',
      `${urls.length} document${urls.length > 1 ? 's' : ''} téléchargé${urls.length > 1 ? 's' : ''}.`,
    );
  } catch (error: unknown) {
    notificationsStore.notifyError(
      'Erreur lors du téléchargement des documents.',
      (error as Error).message,
    );
  } finally {
    downloadingDocuments.value = false;
  }
};

const downloadBulkSignatures = async () => {
  if (!travel.value || checkedParticipants.value.length === 0) return;

  const participationsWithSignature = filteredAndSortedParticipations.value.filter(
    (p) => checkedParticipants.value.includes(p.user!.id) && p.signature?.signedDate,
  );

  if (participationsWithSignature.length === 0) {
    notificationsStore.notifyInfo(
      'Aucun document signé disponible',
      'Aucun des participants sélectionnés n\'a de document signé.',
    );
    return;
  }

  downloadingSignatures.value = true;
  try {
    const urls = participationsWithSignature.map(
      (p) => getFileUrl(p.signature!.signedDocument!.key, true),
    );
    await triggerDownloadsSequentially(urls);
    notificationsStore.notifySuccess(
      'Téléchargements lancés',
      `${urls.length} document${urls.length > 1 ? 's' : ''} signé${urls.length > 1 ? 's' : ''} téléchargé${urls.length > 1 ? 's' : ''}.`,
    );
  } finally {
    downloadingSignatures.value = false;
  }
};

const sendInvitation = () => {
  if (travel.value && checkedParticipants.value.length > 0) {
    adminApi
      .sendParticipantInvitations(travel.value.id, checkedParticipants.value)
      .then((response) => {
        if (checkedParticipants.value.length == 1) {
          notificationsStore.notifySuccess(
            'Invitation envoyée',
            "L'invitation a été envoyée avec succès.",
          );
        } else {
          notificationsStore.notifySuccess(
            'Invitations envoyées',
            `Les invitations ont été envoyées avec succès aux ${checkedParticipants.value.length} participants sélectionnés.`,
          );
        }

        response.data.forEach((participation) => {
          const found = travel.value?.participations?.find(
            (p) => p.user?.id === participation.user?.id,
          );
          if (found) {
            Object.assign(found, participation);
          }
        });
      })
      .catch((error: Error) => {
        notificationsStore.notifyError(
          "Erreur lors de l'envoi des invitations.",
          error.message,
        );
      });
  } else {
    notificationsStore.notifyError("Impossible d'envoyer les invitations.");
  }
};

// Email libre à partir d'un template Brevo choisi par l'administrateur
const showEmailModal = ref(false);
const emailTemplates = ref<EmailTemplateDto[]>([]);
const loadingEmailTemplates = ref(false);
const selectedEmailTemplateId = ref<number | null>(null);
const sendingEmail = ref(false);

const emailTemplateOptions = computed(() => [
  { value: null, text: 'Choisir un template…', disabled: true },
  ...emailTemplates.value.map((template) => ({
    value: template.id,
    text: template.subject ? `${template.name} — ${template.subject}` : template.name,
  })),
]);

const openEmailModal = async () => {
  if (checkedParticipants.value.length === 0) return;
  showEmailModal.value = true;
  if (emailTemplates.value.length > 0) return;

  loadingEmailTemplates.value = true;
  try {
    const response = await adminApi.getEmailTemplates();
    emailTemplates.value = response.data;
  } catch (error) {
    showEmailModal.value = false;
    notificationsStore.notifyError('Impossible de charger les templates Brevo.', (error as Error).message);
  } finally {
    loadingEmailTemplates.value = false;
  }
};

const sendEmail = async () => {
  if (!travel.value || selectedEmailTemplateId.value === null || checkedParticipants.value.length === 0) return;

  sendingEmail.value = true;
  try {
    const response = await adminApi.sendParticipantEmail(travel.value.id, {
      templateId: selectedEmailTemplateId.value,
      userIds: checkedParticipants.value,
    });
    const sent = response.data.sent;
    if (sent === 0) {
      notificationsStore.notifyInfo(
        'Aucun email envoyé',
        "Aucun participant sélectionné ne possède d'adresse email.",
      );
    } else {
      notificationsStore.notifySuccess(
        'Email envoyé',
        `L'email a été envoyé à ${sent} participant${sent > 1 ? 's' : ''}.`,
      );
    }
  } catch (error) {
    notificationsStore.notifyError("Erreur lors de l'envoi de l'email.", (error as Error).message);
  } finally {
    sendingEmail.value = false;
  }
};

const sendReminder = () => {
  if (travel.value && checkedParticipants.value.length > 0) {
    adminApi
      .sendParticipantReminders(travel.value.id, checkedParticipants.value)
      .then((response) => {
        if (response.data.length === 0) {
          notificationsStore.notifyInfo(
            'Aucune relance envoyée',
            'Aucun participant sélectionné n\'est éligible à la relance (inscription déjà finalisée ou refusée).',
          );
          return;
        }
        if (response.data.length == 1) {
          notificationsStore.notifySuccess(
            'Relance envoyée',
            'La relance a été envoyée avec succès.',
          );
        } else {
          notificationsStore.notifySuccess(
            'Relances envoyées',
            `Les relances ont été envoyées avec succès à ${response.data.length} participant${response.data.length > 1 ? 's' : ''}.`,
          );
        }

        response.data.forEach((participation) => {
          const found = travel.value?.participations?.find(
            (p) => p.user?.id === participation.user?.id,
          );
          if (found) {
            Object.assign(found, participation);
          }
        });
      })
      .catch((error: Error) => {
        notificationsStore.notifyError(
          'Erreur lors de l\'envoi des relances.',
          error.message,
        );
      });
  } else {
    notificationsStore.notifyError("Impossible d'envoyer les relances.");
  }
};

// ─── Lifecycle ────────────────────────────────────────────────────────────────

onMounted(() => {
  loadPreferences();
  load();
});

watch(
  () => props.id,
  () => {
    load();
  },
);

watch(
  searchText,
  () => {
    if(searchText.value.trim() !== '') {
      checkedParticipants.value = [];
    }
  },
);
</script>
